import React, { useState, useEffect, useRef } from 'react';
import './Intake.css';
import './MemberView.css';
import { Progress, ChoiceList, ShockTable, TierLadder, money } from './IntakeSteps';
import IntakeJourney from './IntakeJourney';
import IntakeAdmin, { TOKEN_KEY, fmtErr } from './IntakeAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PASS_KEY = 'ik_member_unlocked_v1';
const TOTAL_STEPS = 5;

const Masthead = ({ subtitle }) => (
  <div className="ik-masthead">
    <img src="/logo.png" alt="Pro Stock Money Bowl" className="ik-logo" />
    <div>
      <div className="ik-eyebrow">Grateful Trust · The Serene System</div>
      <h1>Your Placement</h1>
      <div className="ik-quiet" style={{ marginTop: '0.2rem' }}>{subtitle}</div>
    </div>
  </div>
);

const Gate = ({ onMember, onAdmin }) => {
  const [mode, setMode] = useState('member');
  const [passcode, setPasscode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode === 'member') {
        const res = await fetch(`${API_URL}/api/intake/gate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passcode }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(fmtErr(body.detail));
        sessionStorage.setItem(PASS_KEY, passcode);
        onMember(passcode);
      } else {
        const res = await fetch(`${API_URL}/api/intake/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(fmtErr(body.detail));
        sessionStorage.setItem(TOKEN_KEY, body.token);
        onAdmin(body.token);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mv-gate-page" data-testid="intake-gate">
      <div className="mv-gate-card">
        <div className="mv-gate-brand">
          <img src="/logo.png" alt="Pro Stock Money Bowl" className="mv-gate-logo" />
          <div className="mv-gate-titles">
            <div className="mv-gate-eyebrow">GRATEFUL TRUST</div>
            <h1 className="mv-gate-title">Your Placement</h1>
            <div className="mv-gate-subtitle">
              {mode === 'member' ? 'Private · Founding member intake' : 'Administrator access'}
            </div>
          </div>
        </div>

        <form className="mv-gate-form" onSubmit={submit}>
          {mode === 'member' ? (
            <>
              <label className="mv-gate-label" htmlFor="ik-passcode">Enter your access code</label>
              <input
                id="ik-passcode"
                type="password"
                className="mv-gate-input"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                autoComplete="off"
                data-testid="intake-passcode-input"
              />
            </>
          ) : (
            <>
              <label className="mv-gate-label" htmlFor="ik-user">Username</label>
              <input
                id="ik-user"
                type="text"
                className="mv-gate-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                data-testid="intake-admin-user-input"
              />
              <label className="mv-gate-label" htmlFor="ik-pw">Password</label>
              <input
                id="ik-pw"
                type="password"
                className="mv-gate-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="intake-admin-pw-input"
              />
            </>
          )}

          {error && <div className="mv-gate-error" data-testid="intake-gate-error">{error}</div>}

          <button type="submit" className="mv-gate-button" disabled={busy}
            data-testid="intake-gate-submit">
            {busy ? 'Checking…' : 'Unlock'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="ik-btn-link"
            onClick={() => { setMode(mode === 'member' ? 'admin' : 'member'); setError(''); }}
            data-testid="intake-gate-toggle"
          >
            {mode === 'member' ? 'Administrator access' : 'Back to member access'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StepBand = ({ config, value, onChange }) => (
  <div className="ik-card">
    <div className="ik-step-label">Step 1 of {TOTAL_STEPS} · Your allocation</div>
    <h2>How much are you thinking of placing with the system?</h2>
    <p>
      This stays private, and there is no right answer. We ask because the next question
      only works in dollars. A percentage is easy to shrug at, and a dollar figure isn't.
      Pick the range closest to what you have in mind.
    </p>
    <ChoiceList
      options={config.bands.map((b) => ({ key: b.key, label: b.label, note: b.note }))}
      value={value}
      onChange={onChange}
      testPrefix="intake-band"
    />
  </div>
);

const StepJourney = ({ config, band }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <div className="ik-step-label">Step 2 of {TOTAL_STEPS} · The journey</div>
      <h2>Before any questions, watch what this actually feels like.</h2>
      <p>
        Numbers on a page don't prepare anyone. So here is the last ten years of the system,
        scaled to your own amount, playing out from left to right. Watch the dip when it
        comes. That's the part you need to be ready for.
      </p>
      <IntakeJourney journey={config.journey} allocation={b.mid} />
    </div>
  );
};

const StepMagnitude = ({ config, band, value, onChange }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <div className="ik-step-label">Step 3 of {TOTAL_STEPS} · The magnitude</div>
      <h2>Now the two numbers, in your money.</h2>
      <p>
        The left is the deepest fall the system has actually had since 2009. The right is
        deeper, and to be conservative it is the one to plan around, because our record has
        never contained a dot-com-scale collapse. The long-horizon study points to a drop of{' '}
        <span className="ik-accent">up to {Math.abs(config.planning_dd_pct)}%</span> as the
        figure to size against.
      </p>
      <ShockTable shock={b.shock} />
      <p className="ik-quiet" style={{ marginTop: '0.7rem' }}>
        These are paper losses during a drawdown, not realised losses, and you just watched
        one recover. But you have to live through them to get there.
      </p>
      <h2 style={{ marginTop: '1.6rem' }}>
        Picture {money(b.shock.planning.value)} on your statement. What would you actually do?
      </h2>
      <ChoiceList options={config.q1_options} value={value} onChange={onChange}
        testPrefix="intake-q1" />
    </div>
  );
};

const StepGrind = ({ config, value, onChange }) => {
  const ex = config.grind_examples || [];
  return (
    <div className="ik-card">
      <div className="ik-step-label">Step 4 of {TOTAL_STEPS} · The duration</div>
      <h2>Now the harder one. Nothing crashes. You just fall behind.</h2>
      <p>
        There is no crisis. Markets climb steadily for 18 months. The system spends part of
        that stretch sitting in gold and Treasuries, so you finish the period up, but a friend
        who simply bought the Nasdaq and did nothing is up far more than you. Nothing is
        broken. This is the trade-off working exactly as designed, and there is no crash or
        headline to point at.
      </p>
      <p>
        It has happened twice:{' '}
        {ex.map((e, i) => (
          <span key={e.year}>
            {i > 0 ? ' and ' : ''}
            <span className="ik-accent">
              {e.year}, when the system trailed the Nasdaq by {e.shortfall_pp} points
            </span>
          </span>
        ))}
        . Members who left in those years left at the worst possible moment.
      </p>
      <p className="ik-quiet">
        Most people find this far harder than a crash. In a crash everyone suffers together.
        In this, there is nothing to blame except your own decision to be in the system.
      </p>
      <h2 style={{ marginTop: '1.6rem' }}>
        You have lived with this for 18 months. Someone who never used the system has made
        more money than you. How do you feel?
      </h2>
      <ChoiceList options={config.q2_options} value={value} onChange={onChange}
        testPrefix="intake-q2" />
    </div>
  );
};

const StepCommit = ({ config, band, form, setForm }) => {
  const b = config.bands.find((x) => x.key === band);
  return (
    <div className="ik-card">
      <div className="ik-step-label">Step 5 of {TOTAL_STEPS} · Your commitment</div>
      <h2>One promise, made while everything is calm.</h2>
      <p>
        This is the only cheap time to meet that number. Signing is not a contract and it
        costs you nothing legally. It exists so that when a drawdown arrives, we can show
        you what you decided before fear had a vote.
      </p>

      <div className="ik-field">
        <label className="ik-label" htmlFor="ik-name">Your full name</label>
        <input id="ik-name" className="ik-input" type="text" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Jane Doe" data-testid="intake-name-input" />
      </div>
      <div className="ik-field">
        <label className="ik-label" htmlFor="ik-email">Email</label>
        <input id="ik-email" className="ik-input" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="jane@example.com" data-testid="intake-email-input" />
      </div>

      <div className="ik-commit">
        <p className="ik-commit-quote">
          “I have seen what a drop of up to {Math.abs(config.planning_dd_pct)}% looks like on
          my own allocation: {money(b.shock.planning.value)}, down{' '}
          {money(b.shock.planning.loss)}. I will not sell when I see it arrive.”
        </p>
        <label className="ik-check">
          <input type="checkbox" checked={form.signed}
            onChange={(e) => setForm({ ...form, signed: e.target.checked })}
            data-testid="intake-commit-checkbox" />
          <span>I've read that, I understand it, and I commit to it.</span>
        </label>
      </div>

      {form.signed && (
        <div className="ik-field">
          <label className="ik-label" htmlFor="ik-sig">Sign by typing your name</label>
          <input id="ik-sig" className="ik-input ik-signature" type="text"
            value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
            placeholder="Jane Doe" data-testid="intake-signature-input" />
        </div>
      )}

      {!form.signed && (
        <p className="ik-quiet">
          You can continue without signing. We'll step you down one tier, which is the right
          thing to do, and you can retake this whenever you like.
        </p>
      )}
    </div>
  );
};

const SplitTile = ({ pct, label, hint, isCore, active, onToggle, onHover, pctTestid, testid }) => {
  // Mouse users get hover. Touch users get tap. Without the pointerType guard the synthetic
  // mouse-enter opens the tip and the click immediately toggles it straight back shut.
  const hovering = useRef(false);
  return (
    <button
      type="button"
      className={`ik-split-cell ${isCore ? 'is-core' : ''} ${active ? 'is-open' : ''}`}
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') { hovering.current = true; onHover(true); }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') { hovering.current = false; onHover(false); }
      }}
      onClick={() => { if (!hovering.current) onToggle(); }}
      aria-expanded={active}
      data-testid={testid}
    >
      <span className="ik-split-pct" data-testid={pctTestid}>{pct}%</span>
      <span className="ik-split-key">
        {label}
        <span className="ik-info" aria-hidden="true">i</span>
      </span>
      <span className="ik-split-hint">{hint}</span>
    </button>
  );
};

const Result = ({ config, result, onRestart }) => {
  const p = result.placement;
  const [tip, setTip] = useState(null);
  const pick = (k) => setTip((t) => (t === k ? null : k));
  return (
    <>
      <div className="ik-card" data-testid="intake-result">
        <div className="ik-step-label">Your placement</div>
        <div className="ik-tier-badge">
          <span className="ik-tier-badge-num">Tier {p.tier}</span>
          <span className="ik-tier-badge-name" data-testid="intake-result-tier-name">{p.name}</span>
        </div>
        <h2>{p.summary}</h2>
        <p className="ik-quiet">
          All of it is your money, in your own account. Tap either box below to see what it
          means.
        </p>
        <div className="ik-split">
          <SplitTile
            pct={p.core}
            label="Core"
            hint="Follows the system"
            isCore
            active={tip === 'core'}
            onToggle={() => pick('core')}
            onHover={(on) => setTip(on ? 'core' : null)}
            pctTestid="intake-result-core"
            testid="intake-result-core-tile"
          />
          <SplitTile
            pct={p.explore}
            label="Explore"
            hint="You decide"
            active={tip === 'explore'}
            onToggle={() => pick('explore')}
            onHover={(on) => setTip(on ? 'explore' : null)}
            pctTestid="intake-result-explore"
            testid="intake-result-explore-tile"
          />
        </div>
        {tip && (
          <div className={`ik-tip ${tip === 'core' ? 'is-core' : ''}`} role="tooltip"
            data-testid={`intake-result-${tip}-tile-tip`}>
            {config.tips[tip]}
          </div>
        )}
        <p>{p.detail}</p>
        {p.reasons.map((r, i) => (
          <div className="ik-reason" key={i} data-testid={`intake-result-reason-${i}`}>{r}</div>
        ))}
      </div>

      <div className="ik-card">
        <div className="ik-step-label">The ladder</div>
        <h2>You can move. That's the point.</h2>
        <p>
          If a drawdown turns out to be harder than you expected, step down a tier. Don't step
          out the door. Leaving at the bottom is the one decision this system cannot protect
          you from.
        </p>
        <TierLadder tiers={config.tiers} current={p.tier} />
      </div>

      <div className="ik-card">
        <div className="ik-step-label">What Explore means</div>
        <h2>Every dollar here is yours. Core is simply the part that follows the rules.</h2>
        <p>
          The money in Core sits in your own account and follows the Serene System. The Explore
          share is the rest, and you direct it. Leave it in cash or a money market, or put it
          into crypto, property, individual stocks, or whatever you prefer. If you would like
          help with that part, we run programs that teach you ways to explore.
        </p>
        <p>
          One thing to be clear about: Explore is not a safety cushion. Putting less into Core
          means a smaller share of your money follows the rules in this system, and nothing
          more than that. Whatever you choose to do with your Explore share can fall further
          and faster than Core does.
        </p>
        <p className="ik-quiet">
          Your answers are saved. We'll use them to decide what to tell you, and when, during
          the stretches that actually test people, the kind you watched play out earlier.
        </p>
        <div className="ik-actions">
          <button type="button" className="ik-btn ik-btn-ghost" onClick={onRestart}
            data-testid="intake-restart">Retake</button>
        </div>
      </div>
    </>
  );
};

const Intake = () => {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(PASS_KEY) || '');
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [step, setStep] = useState(0);
  const [band, setBand] = useState(null);
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', signed: false, signature: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/intake/config`)
      .then((r) => r.json())
      .then(setConfig)
      .catch(() => setConfigError('Could not load the diagnostic. Please refresh.'));
  }, []);

  const canAdvance = () => {
    if (step === 0) return !!band;
    if (step === 1) return true;
    if (step === 2) return !!q1;
    if (step === 3) return !!q2;
    if (step === 4) {
      const base = form.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email);
      return base && (!form.signed || form.signature.trim().length >= 2);
    }
    return false;
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/intake/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passcode,
          name: form.name.trim(),
          email: form.email.trim(),
          band,
          q1,
          q2,
          precommit_signed: form.signed,
          precommit_name: form.signed ? form.signature.trim() : null,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(fmtErr(body.detail));
      setResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setResult(null);
    setStep(0);
    setBand(null);
    setQ1(null);
    setQ2(null);
    setForm({ name: '', email: '', signed: false, signature: '' });
  };

  const signOutAdmin = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAdminToken('');
  };

  if (adminToken) {
    return (
      <div className="ik-page ik-page-admin" data-testid="intake-page-admin">
        <div className="ik-shell">
          <Masthead subtitle="Administrator · all member records" />
          <IntakeAdmin token={adminToken} onSignOut={signOutAdmin} />
        </div>
      </div>
    );
  }

  if (!passcode) {
    return <Gate onMember={setPasscode} onAdmin={setAdminToken} />;
  }

  if (configError) {
    return (
      <div className="ik-page">
        <div className="ik-shell">
          <div className="ik-error">{configError}</div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="ik-page" data-testid="intake-loading">
        <div className="ik-shell"><div className="ik-empty">Loading…</div></div>
      </div>
    );
  }

  return (
    <div className="ik-page" data-testid="intake-page">
      <div className="ik-shell">
        <Masthead
          subtitle={result
            ? 'Three questions, answered. Here is where you land.'
            : 'Watch the journey, then answer three questions. About two minutes, in dollars rather than feelings.'}
        />

        {result ? (
          <Result config={config} result={result} onRestart={restart} />
        ) : (
          <>
            <Progress step={step} total={TOTAL_STEPS} />

            {step === 0 && <StepBand config={config} value={band} onChange={setBand} />}
            {step === 1 && <StepJourney config={config} band={band} />}
            {step === 2 && (
              <StepMagnitude config={config} band={band} value={q1} onChange={setQ1} />
            )}
            {step === 3 && <StepGrind config={config} value={q2} onChange={setQ2} />}
            {step === 4 && (
              <StepCommit config={config} band={band} form={form} setForm={setForm} />
            )}

            {error && <div className="ik-error" data-testid="intake-submit-error">{error}</div>}

            <div className="ik-actions">
              {step > 0 && (
                <button type="button" className="ik-btn ik-btn-ghost"
                  onClick={() => setStep(step - 1)} data-testid="intake-back">Back</button>
              )}
              <button
                type="button"
                className="ik-btn ik-btn-primary"
                disabled={!canAdvance() || busy}
                onClick={() => (step === 4 ? submit() : setStep(step + 1))}
                data-testid="intake-next"
              >
                {busy ? 'Saving…' : step === 4 ? 'See my placement' : 'Continue'}
              </button>
            </div>

            <div className="ik-footnote">
              Nothing here is investment advice, and none of it is a promise of future
              returns. Past results, including every figure on this page, describe what has
              already happened, not what will.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Intake;
